import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Collapse, Dialog, DialogContent, DialogTitle, DialogActions, Button, TextField, List, ListItem, ListItemButton, ListItemText, ListItemIcon, RadioGroup, Radio, FormControlLabel, Avatar, IconButton, Typography, Box, Switch, MenuItem, CircularProgress, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Check, Close, ExpandLess, ExpandMore, Person, Search } from '@mui/icons-material';
import styled from "@emotion/styled";
import { useDispatch } from 'react-redux';
import { getAccountMembers } from '../redux/ducks/account';
import { getAllUsers } from '../redux/slices/users';
import { setProjectAssignees, setProjectResponsible } from '../redux/slices/project';
import { setTaskAssignees, setTaskResponsible, getTask } from '../redux/ducks/task';
import { getRoles } from '../redux/ducks/role';
import { getVisibilityCheck } from '../redux/ducks/permission';
import { showAlert } from '../redux/slices/alerts';
import { useSelector } from 'react-redux';
import { useHasPermission } from '../utils/permissionUtils';
import { getAvatarUrl } from '../utils/avatarUtils';

const StyledDialog = styled(Dialog)(({ theme }) => ({

}));

const LoadingContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
}));

const ScrollableList = styled(List)(({ theme }) => ({
    maxHeight: 288,
    minHeight: 288,
    overflow: 'auto',
    margin: theme.spacing(0),
    borderRadius: theme.shape.borderRadius,
}));

const AssignedScrollableList = styled(List)(({ theme }) => ({
    maxHeight: 335,
    minHeight: 335,
    overflow: 'auto',
    margin: theme.spacing(0),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1, 0),
}));

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1, 0),
}));

const severityStyles = (theme, severity) => {
    if (severity === 'error') {
        return { backgroundColor: alpha(theme.palette.error.main, 0.12), borderColor: theme.palette.error.main };
    }
    if (severity === 'warning') {
        return { backgroundColor: alpha(theme.palette.warning.main, 0.12), borderColor: theme.palette.warning.main };
    }
    return {};
};

const UserListItem = styled(ListItem, { shouldForwardProp: (prop) => prop !== 'severity' })(({ theme, severity }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    gap: theme.spacing(1),
    ...severityStyles(theme, severity),
}));

const AvailableRow = styled(ListItemButton, { shouldForwardProp: (prop) => prop !== 'severity' })(({ theme, severity }) => ({
    borderRadius: theme.shape.borderRadius,
    border: '1px solid transparent',
    ...severityStyles(theme, severity),
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    padding: theme.spacing(2),
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(0),
    display: 'flex',
    gap: theme.spacing(1),
}));

const SelectorBox = styled(Box)(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(1, 2),
}));

const ClientAccordion = styled(ListItemButton)(({ theme }) => ({
    marginLeft: theme.spacing(-1),
    padding: theme.spacing(0),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
}));

const LeftSelectorBox = styled(SelectorBox)(({ theme }) => ({
    borderRight: `1px solid ${theme.palette.divider}`,
}));

const SearchRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    padding: theme.spacing(1),
}));

const SearchIcon = styled(Search)(({ theme }) => ({
    color: theme.palette.action.active,
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
}));

const ClientHeading = styled(ListItemText)(({ theme }) => ({
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
}));

const SmallCloseIcon = styled(Close)(({ theme }) => ({
    fontSize: theme.typography.size.base,
}));

const EmptySelectionText = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(2),
    textAlign: 'center',
}));

const RoleColumn = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexDirection: 'column',
}));

const ActionGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
}));

const UserAssignmentDialog = ({isOpen, onClose, objectId, objectType, onAssign, isNewObject = false, accountId, users, useRoles = false, singleSelection = false, maxRoleLevel = 0, globalUsers = false }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const userRoleLevel = useSelector(state => state.auth.currentUser?.role?.level ?? Infinity);
    const [availablePeople, setAvailablePeople] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [assigned, setAssigned] = useState([]);
    const [responsible, setResponsible] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showInactiveUsers, setShowInactiveUsers] = useState(false);
    const canSeeInternal = hasPermission('canSeeInternal');
    const [openGroups, setOpenGroups] = useState({});
    const [roles, setRoles] = useState([]);
    const [visibleGroupsLimit, setVisibleGroupsLimit] = useState(20);
    const [effectivePrivacy, setEffectivePrivacy] = useState('Public');
    const [permsByUser, setPermsByUser] = useState({});
    const project = useSelector(state => state.project.currentProject);
    const effectiveSearchTerm = searchTerm.trim().length >= 2 ? searchTerm.trim() : '';

    const visibilityTarget = useMemo(() => {
        if (objectType === 'task') {
            return isNewObject ? { type: 'project', id: objectId } : { type: 'task', id: objectId };
        }
        if (objectType === 'project') {
            return isNewObject ? { type: 'account', id: objectId } : { type: 'project', id: objectId };
        }
        if (objectType === 'account') {
            return isNewObject ? { type: 'client', id: objectId } : { type: 'account', id: accountId || objectId };
        }
        if (objectType === 'client') {
            return { type: 'client', id: objectId };
        }
        return null;
    }, [objectType, isNewObject, objectId, accountId]);

    const assignableRoles = useCallback((user) => roles.filter((role) => (user.client?.isInternal ? role.type === 'Internal' : role.type === 'General') && role.level >= userRoleLevel), [roles, userRoleLevel]);

    // The least privileged role of the user's type; equal levels go to the oldest role.
    const defaultRoleFor = useCallback((user) => {
        const [role] = [...assignableRoles(user)].sort((a, b) => b.level - a.level || String(a._id).localeCompare(String(b._id)));
        return role?.name || '';
    }, [assignableRoles]);

    useEffect(() => {
        if (!useRoles || roles.length === 0) return;
        setAssigned((prev) => (prev.some((u) => u && !u.accountRole) ? prev.map((u) => (u && !u.accountRole ? { ...u, accountRole: defaultRoleFor(u) } : u)) : prev));
    }, [useRoles, roles, defaultRoleFor]);

    const TYPE_LABELS = { client: 'This client', account: 'This account', project: 'This project', task: 'This task' };
    const typeLabel = TYPE_LABELS[objectType] || 'This item';

    const severityFor = (user) => {
        const p = permsByUser[user._id];
        if (!p) return null;
        if (effectivePrivacy === 'Private' && !p.canSeePrivate) return 'error';
        if (effectivePrivacy === 'Internal' && !(p.canSeeInternal || p.canSeePrivate)) return 'warning';
        return null;
    };

    const tooltipFor = (user) => {
        const severity = severityFor(user);
        if (severity === 'error') {
            return `${typeLabel} is Private — ${user.fullName} lacks permission to view Private items and won't see it once assigned.`;
        }
        if (severity === 'warning') {
            return `${typeLabel} is Internal — ${user.fullName} can't view Internal items.`;
        }
        return '';
    };

    useEffect(() => {
        if (isOpen) return;
        setAvailablePeople([]);
        setAssigned([]);
        setResponsible(null);
        setSearchTerm('');
        setOpenGroups({});
        setVisibleGroupsLimit(20);
        setPermsByUser({});
        setEffectivePrivacy('Public');
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        dispatch(getRoles((data) => {
            setRoles(data.filter(role => role.level >= maxRoleLevel));
        }));
    }, [isOpen, dispatch, maxRoleLevel]);

    useEffect(() => {
        if (!isOpen || isNewObject) return;
        if (objectType === 'project') {
            if (!project) {
                dispatch(showAlert({ severity: 'warning', message: 'Project not found' }));
                onClose();
                return;
            }
            const responsibleUser = project.responsible || null;
            if (singleSelection) {
                setResponsible(responsibleUser);
                setAssigned(responsibleUser ? [responsibleUser] : []);
            } else {
                const assignedUsers = project.assignees || [];
                const updatedAssigned = responsibleUser && !assignedUsers.some(u => u._id === responsibleUser._id)
                    ? [responsibleUser, ...assignedUsers]
                    : assignedUsers;
                setAssigned(updatedAssigned);
                setResponsible(responsibleUser);
            }
            const fetchAccountId = project.account?._id || accountId;
            if (fetchAccountId) {
                dispatch(getAccountMembers(fetchAccountId, setAvailablePeople));
            }
        } else if (objectType === 'task') {
            dispatch(getTask(objectId, (result, error) => {
                if (!result) {
                    if (!error) dispatch(showAlert({ severity: 'warning', message: 'Task not found' }));
                    onClose();
                    return;
                }
                const responsibleUser = result.responsible || null;
                if (singleSelection) {
                    setResponsible(responsibleUser);
                    setAssigned(responsibleUser ? [responsibleUser] : []);
                } else {
                    const assignedUsers = result.assignees || [];
                    const updatedAssigned = responsibleUser && !assignedUsers.some(u => u._id === responsibleUser._id)
                        ? [responsibleUser, ...assignedUsers]
                        : assignedUsers;
                    setAssigned(updatedAssigned);
                    setResponsible(responsibleUser);
                }
                const fetchAccountId = result.account?._id;
                if (fetchAccountId) {
                    dispatch(getAccountMembers(fetchAccountId, setAvailablePeople));
                }
            }));
        } else if (objectType === 'account') {
            dispatch(getAccountMembers(accountId, setAvailablePeople));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isNewObject, objectType, objectId, project?._id, accountId, singleSelection, dispatch]);

    useEffect(() => {
        if (!isOpen || !isNewObject) return;
        if (users && Array.isArray(users) && users.length > 0) {
            setAvailablePeople(users);
            return;
        }
        if (!accountId) return;
        if (globalUsers) {
            setIsLoading(true);
            dispatch(getAllUsers({ callback: (data) => {
                if (!data) {
                    setIsLoading(false);
                    return;
                }
                dispatch(getAccountMembers(accountId, (accountMembers) => {
                    setAvailablePeople(data.filter(user => !accountMembers.some(am => am._id === user._id)));
                    setIsLoading(false);
                }));
            }}));
        } else {
            dispatch(getAccountMembers(accountId, setAvailablePeople));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isNewObject, accountId, globalUsers, users?.length, dispatch]);

    const groupedUsers = useMemo(() => {
        return availablePeople.reduce((acc, user) => {
            const clientName = user.client?.name || 'Unassigned';
            if (!acc[clientName]) {
                acc[clientName] = [];
            }
            acc[clientName].push(user);
            return acc;
        }, {});
    }, [availablePeople]);

    useEffect(() => {
        if (globalUsers && Object.keys(groupedUsers).length > 0 && Object.keys(openGroups).length === 0) {
            const clientNames = Object.keys(groupedUsers);
            const initialOpenGroups = {};
            clientNames.slice(0, 2).forEach(clientName => {
                initialOpenGroups[clientName] = true;
            });
            setOpenGroups(initialOpenGroups);
        } else if (!globalUsers && Object.keys(groupedUsers).length > 0) {
            const allOpen = {};
            Object.keys(groupedUsers).forEach(clientName => {
                allOpen[clientName] = true;
            });
            setOpenGroups(prev => {
                if (Object.keys(prev).length === 0) {
                    return allOpen;
                }
                return prev;
            });
        }
    }, [groupedUsers, globalUsers, openGroups]);

    useEffect(() => {
        if (!isOpen) return;
        const userIds = [
            ...availablePeople.map((u) => u._id),
            ...assigned.map((u) => u?._id),
        ].filter((id) => id && !permsByUser[id]);
        const uniqueIds = [...new Set(userIds)];
        if (uniqueIds.length === 0) return;

        dispatch(getVisibilityCheck({ target: visibilityTarget, userIds: uniqueIds }, (data) => {
            if (!data) return;
            setEffectivePrivacy(data.effectivePrivacy || 'Public');
            setPermsByUser((prev) => ({ ...prev, ...(data.users || {}) }));
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, availablePeople, assigned, visibilityTarget, dispatch]);

    const handleAssignUser = (user) => {
        if (user) {
            if (singleSelection) {
                // In single selection mode, only one person can be selected as responsible
                setResponsible(user);
                setAssigned([user]);
            } else {
                // Normal mode: add to assignees
                if (!assigned.some(u => u?._id === user._id)) {
                    const newAssigned = [...assigned, useRoles ? { ...user, accountRole: defaultRoleFor(user) } : user];
                    setAssigned(newAssigned);

                    if (newAssigned.length === 1) {
                        setResponsible(user);
                    }
                }
            }
        }
    };

    const handleUnassignUser = (userId) => {
        if (singleSelection) {
            // In single selection mode, clearing removes both responsible and assigned
            setResponsible(null);
            setAssigned([]);
        } else {
            const newAssigned = assigned.filter(u => u?._id !== userId);
            setAssigned(newAssigned);

            if (responsible?._id === userId) {
                setResponsible(newAssigned.length > 0 ? newAssigned[0] : null);
            }
        }
    };

    const handleSetResponsible = (event) => {
        const userId = event.target.value;
        const user = assigned.find(u => u._id === userId);
        setResponsible(user || null);
    };

    const handleAssign = () => {
        if (isNewObject) {
            if (onAssign) {
                if (singleSelection) {
                    // In single selection mode, only pass responsible (no assignees)
                    onAssign(objectId, [], responsible);
                } else {
                    onAssign(objectId, assigned, responsible);
                }
            }
            onClose();
        } else {
            const setResponsibleAction = objectType === 'project' ? setProjectResponsible : setTaskResponsible;
            const setAssigneesAction = objectType === 'project' ? setProjectAssignees : setTaskAssignees;

            if (objectType === 'project') {
                if (singleSelection) {
                    // In single selection mode, only set responsible, no assignees
                    dispatch(setAssigneesAction({ projectId: objectId, responsible: responsible?._id, assignees: [] }));
                } else {
                    dispatch(setAssigneesAction({ projectId: objectId, responsible: responsible?._id, assignees: assigned.map(user => user._id) }));
                }
                onClose();
            } else {
                dispatch(setResponsibleAction(objectId, responsible?._id, () => {
                    if (singleSelection) {
                        // In single selection mode, set empty assignees
                        dispatch(setAssigneesAction(objectId, [], () => {
                            if (onAssign)
                                onAssign(objectId, [], responsible);
                            onClose();
                        }));
                    } else {
                        dispatch(setAssigneesAction(objectId, assigned.map(user => user._id), () => {
                            if (onAssign)
                                onAssign(objectId, assigned, responsible);
                            onClose();
                        }));
                    }
                }));
            }
        }
    };

    const handleAssignRole = (event, user) => {
        setAssigned((prev) => prev.map((u) => (u._id === user._id ? { ...u, accountRole: event.target.value } : u)));
    };

    const handleScroll = (e) => {
        const target = e.target;
        const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        
        if (scrollBottom < 100 && !effectiveSearchTerm && Object.keys(groupedUsers).length > visibleGroupsLimit) {
            setVisibleGroupsLimit(prev => prev + 20);
        }
    };

    return (
        <StyledDialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <StyledDialogTitle>Assign Users</StyledDialogTitle>
            <StyledDialogContent dividers>
                    <LeftSelectorBox>
                        <Typography variant="h6">Available Users</Typography>
                        <SearchRow>
                            <SearchIcon />
                            <TextField
                                fullWidth
                                variant="standard"
                                placeholder="Search users..."
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </SearchRow>
                        {isLoading ? (
                            <LoadingContainer>
                                <CircularProgress size="18px" /> <Typography variant="body2" color="text.secondary">Loading users...</Typography>
                            </LoadingContainer>
                        ) : (
                            <ScrollableList 
                                key="available-users" 
                                dense 
                                onScroll={handleScroll}
                            >
                                {(() => {
                                    const hasSearchTerm = effectiveSearchTerm.length > 0;
                                    const groupEntries = Object.entries(groupedUsers);
                                    
                                    const groupsToRender = hasSearchTerm 
                                        ? groupEntries 
                                        : groupEntries.slice(0, visibleGroupsLimit);
                                    
                                    return groupsToRender.map(([clientName, allUsers]) => {
                                        const isGroupOpen = openGroups[clientName] || false;
                                        const shouldProcessUsers = hasSearchTerm || !globalUsers || isGroupOpen;
                                        
                                        const filteredUsers = shouldProcessUsers ? allUsers.filter(user =>
                                            user.fullName.toLowerCase().includes(effectiveSearchTerm.toLowerCase()) &&
                                            (showInactiveUsers || user.active)
                                        ) : [];
                                        
                                        if (filteredUsers.length === 0 && shouldProcessUsers) {
                                            return null;
                                        }
                                        
                                        return (
                                            <React.Fragment key={clientName}>
                                                <ClientAccordion onClick={() => setOpenGroups(prev => ({...prev, [clientName]: !prev[clientName]}))}>
                                                    <ClientHeading variant="subtitle1">{clientName}</ClientHeading>
                                                    {isGroupOpen ? <ExpandLess /> : <ExpandMore />}
                                                </ClientAccordion>
                                                {filteredUsers.map(user => {
                                                    const severity = severityFor(user);
                                                    const row = (
                                                        <AvailableRow
                                                            severity={severity}
                                                            onClick={() => handleAssignUser(user)}
                                                        >
                                                            <ListItemIcon>
                                                                <Avatar src={getAvatarUrl(user)}>
                                                                    <Person />
                                                                </Avatar>
                                                            </ListItemIcon>
                                                            <ListItemText primary={user?.fullName} secondary={user?.role?.name} />
                                                            {(singleSelection ? responsible?._id === user._id : assigned.some(u => u?._id === user._id)) && (
                                                                <Check color="success" />
                                                            )}
                                                        </AvailableRow>
                                                    );
                                                    return (
                                                        <Collapse key={user._id} in={isGroupOpen || hasSearchTerm}>
                                                            {severity ? (
                                                                <Tooltip title={tooltipFor(user)}>
                                                                    <span>{row}</span>
                                                                </Tooltip>
                                                            ) : row}
                                                        </Collapse>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    });
                                })()}
                            </ScrollableList>
                        )}
                    </LeftSelectorBox>
                    <SelectorBox >
                        <Typography variant="h6">{singleSelection ? 'Selected User' : 'Assigned Users'}</Typography>
                        <AssignedScrollableList dense>
                            {singleSelection ? (
                                // Single selection mode: show only the responsible user
                                responsible ? (
                                    <Tooltip title={tooltipFor(responsible)} disableHoverListener={!severityFor(responsible)}>
                                    <UserListItem severity={severityFor(responsible)}>
                                        <ListItemText
                                            primary={responsible?.fullName}
                                            secondary="Responsible"
                                        />
                                        <IconButton
                                            edge="end"
                                            size="small"
                                            onClick={() => handleUnassignUser(responsible._id)}
                                        >
                                            <SmallCloseIcon />
                                        </IconButton>
                                    </UserListItem>
                                    </Tooltip>
                                ) : (
                                    <EmptySelectionText variant="body2" color="text.secondary">
                                        No user selected
                                    </EmptySelectionText>
                                )
                            ) : useRoles ? (
                                assigned.map(user => (
                                    <Tooltip key={user._id} title={tooltipFor(user)} disableHoverListener={!severityFor(user)}>
                                    <UserListItem severity={severityFor(user)}>
                                        <RoleColumn>
                                            <ListItemText primary={user?.fullName} secondary={user?.role?.name} />

                                                <TextField
                                                    size="small"
                                                    value={user.accountRole || ''}
                                                    onChange={(e) => handleAssignRole(e, user)}
                                                    select
                                                >
                                                    {assignableRoles(user)
                                                        .map(role => (
                                                            <MenuItem key={role._id} value={role.name}>{role.name}</MenuItem>
                                                        ))
                                                    }
                                                </TextField>
                                        </RoleColumn>
                                        <IconButton edge="end" size="small" onClick={() => handleUnassignUser(user._id)} >
                                            <SmallCloseIcon />
                                        </IconButton>
                                    </UserListItem>
                                    </Tooltip>
                                ))
                            ):(
                                assigned.map(user => (
                                    <StyledRadioGroup key={`radio-group-${user._id}`} value={responsible?._id || ''} onChange={handleSetResponsible}>
                                    <Tooltip title={tooltipFor(user)} disableHoverListener={!severityFor(user)}>
                                    <UserListItem severity={severityFor(user)} selected={responsible?._id === user._id} key={user._id}>
                                        <FormControlLabel
                                            value={user._id}
                                            control={<Radio />}
                                            label={<ListItemText primary={user?.fullName} secondary={responsible?._id === user._id ? 'Responsible' : ''} />}
                                        />
                                        <IconButton
                                            edge="end"
                                            size="small"
                                            onClick={() => handleUnassignUser(user._id)}
                                        >
                                            <SmallCloseIcon />
                                        </IconButton>
                                    </UserListItem>
                                    </Tooltip>
                                    </StyledRadioGroup>
                                ))
                            )}
                        </AssignedScrollableList>
                    </SelectorBox>
            </StyledDialogContent>
            <StyledDialogActions>
                {canSeeInternal ? (
                    <FormControlLabel control={<Switch onChange={() => setShowInactiveUsers(!showInactiveUsers)} />} label="Show Inactive Users" />
                ) : (
                    <Box />
                )}
                <ActionGroup>
                    <Button variant="outlined" onClick={onClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleAssign}>Assign</Button>
                </ActionGroup>
            </StyledDialogActions>
        </StyledDialog>
    );
}

export default UserAssignmentDialog;